/**
 * Utility to detect URLs in text and render them as clickable links
 */

// URL regex pattern that matches common URL formats
const URL_REGEX = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s]|www\.[^\s<]+[^<.,:;"')\]\s])/gi;

/**
 * Checks if a string contains URLs
 * @param {string} text - Text to check
 * @returns {boolean} - True if text contains URLs
 */
export const containsLinks = (text) => {
  if (!text || typeof text !== 'string') return false;
  return URL_REGEX.test(text);
};

/**
 * Parses text and returns an array of text and link segments
 * @param {string} text - Text to parse
 * @returns {Array} - Array of {type: 'text'|'link', content: string} objects
 */
export const parseLinks = (text) => {
  if (!text || typeof text !== 'string') return [{ type: 'text', content: text || '' }];
  
  const segments = [];
  let lastIndex = 0;
  
  // Reset regex state
  URL_REGEX.lastIndex = 0;
  
  let match;
  while ((match = URL_REGEX.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }
    
    // Add the link
    let url = match[0];
    // Ensure URL has protocol
    if (url.startsWith('www.')) {
      url = 'https://' + url;
    }
    
    segments.push({
      type: 'link',
      content: match[0],
      href: url
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last link
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }
  
  // If no links found, return original text
  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }
  
  return segments;
};

/**
 * Default link styles
 */
export const linkStyles = {
  default: 'text-blue-500 hover:text-blue-700 underline cursor-pointer',
  light: 'text-blue-300 hover:text-blue-100 underline cursor-pointer',
};

export default { containsLinks, parseLinks, linkStyles };
